import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Firestore collection names
const CLASSES_COLLECTION = 'classes';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return db && process.env.REACT_APP_FIREBASE_API_KEY && 
         process.env.REACT_APP_FIREBASE_API_KEY !== 'your_api_key_here';
};

export interface Class {
  id?: string;
  name: string;
  grade: string;
  teacherId: string;
  description?: string;
  academicYear?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

class ClassService {
  // For backward compatibility when Firebase is not configured
  private fallbackClasses: Class[] = [
    {
      id: 'class-1',
      name: 'Classe A',
      grade: 'CP',
      teacherId: 'teacher-1',
      description: 'Classe de CP',
      academicYear: '2024-2025',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'class-2',
      name: 'Classe B',
      grade: 'CE1',
      teacherId: 'teacher-1',
      description: 'Classe de CE1',
      academicYear: '2024-2025',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'class-3',
      name: 'Classe C',
      grade: 'CE2',
      teacherId: 'teacher-1',
      description: 'Classe de CE2',
      academicYear: '2024-2025',
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  // Generate unique IDs
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Get all classes for a teacher
  async getClassesByTeacher(teacherId: string): Promise<Class[]> {
    try {
      if (isFirebaseConfigured()) {
        const q = query(
          collection(db, CLASSES_COLLECTION),
          where('teacherId', '==', teacherId),
          where('isActive', '==', true),
          orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as Class[];
      } else {
        return this.fallbackClasses.filter(c => c.teacherId === teacherId && c.isActive);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      return [];
    }
  }

  // Get a class by ID
  async getClassById(classId: string): Promise<Class | null> {
    try {
      if (isFirebaseConfigured()) {
        const docSnap = await getDocs(query(collection(db, CLASSES_COLLECTION), where('__name__', '==', classId)));
        if (!docSnap.empty) {
          const data = docSnap.docs[0].data();
          return {
            id: docSnap.docs[0].id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Class;
        }
      } else {
        return this.fallbackClasses.find(c => c.id === classId) || null;
      }
    } catch (error) {
      console.error('Error fetching class:', error);
    }
    return null;
  }

  // Create a new class
  async createClass(classData: Omit<Class, 'id' | 'createdAt' | 'updatedAt'>): Promise<Class> {
    const newClass: Class = {
      ...classData,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      if (isFirebaseConfigured()) {
        const docRef = await addDoc(collection(db, CLASSES_COLLECTION), {
          ...newClass,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        newClass.id = docRef.id;
      } else {
        this.fallbackClasses.push(newClass);
      }
      
      return newClass;
    } catch (error) {
      console.error('Error creating class:', error);
      throw new Error('Failed to create class');
    }
  }

  // Update a class
  async updateClass(classId: string, updates: Partial<Class>): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        const docRef = doc(db, CLASSES_COLLECTION, classId);
        await updateDoc(docRef, {
          ...updates,
          updatedAt: Timestamp.now()
        });
      } else {
        const index = this.fallbackClasses.findIndex(c => c.id === classId);
        if (index !== -1) {
          this.fallbackClasses[index] = {
            ...this.fallbackClasses[index],
            ...updates,
            updatedAt: new Date()
          };
        }
      }
    } catch (error) {
      console.error('Error updating class:', error);
      throw new Error('Failed to update class');
    }
  }

  // Delete a class
  async deleteClass(classId: string): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        await deleteDoc(doc(db, CLASSES_COLLECTION, classId));
      } else {
        this.fallbackClasses = this.fallbackClasses.filter(c => c.id !== classId);
      }
    } catch (error) {
      console.error('Error deleting class:', error);
      throw new Error('Failed to delete class');
    }
  }
}

export const classService = new ClassService();
